import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../lib/format';

export interface PayrollFormState {
  employee_id: string;
  basic_salary: string | number;
  taxable_allowance: string | number;
  non_taxable_allowance: string | number;
  other_deductions: string | number;
  payroll_month: string;
}

export interface PayrollEmployeeOption {
  id: number | string;
  full_name: string;
  employee_code: string;
  basic_salary?: number | string;
  taxable_allowance?: number | string;
  non_taxable_allowance?: number | string;
  other_deductions?: number | string;
}

export interface PayrollCalculationResult {
  employee_name: string;
  gross: number;
  taxable_allowance: number;
  non_taxable_allowance: number;
  other_deductions: number;
  income_tax: number;
  pension_employee: number;
  pension_employer: number;
  net_pay: number;
  payout_status?: string | null;
}

export interface PayrollBatchResultItem {
  employee_name: string;
  gross: number;
  net_pay: number;
}

export interface RunPayrollCardProps {
  form: PayrollFormState;
  setForm: Dispatch<SetStateAction<PayrollFormState>>;
  updateField: (field: keyof PayrollFormState, value: string) => void;
  employees: PayrollEmployeeOption[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onBatchRun: () => void;
  onExportPayslip: () => void;
  resultRecordId: number | string | null | undefined;
  result: PayrollCalculationResult | null | undefined;
  batchResults: PayrollBatchResultItem[];
  activeLanguage: string;
  error?: string | null;
}

export const RunPayrollCard: FC<RunPayrollCardProps> = ({
  form,
  setForm,
  updateField,
  employees,
  isSubmitting,
  onSubmit,
  onBatchRun,
  onExportPayslip,
  resultRecordId,
  result,
  batchResults,
  activeLanguage,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <form className="card" onSubmit={onSubmit}>
        <h2>{t('runPayroll')}</h2>

        <label>
          {t('employee')}
          <select
            value={form.employee_id}
            onChange={(event) => {
              const nextId = event.target.value;
              const employee = employees.find((item) => item.id === Number(nextId));
              setForm((current) => ({
                ...current,
                employee_id: nextId,
                basic_salary: employee?.basic_salary ?? current.basic_salary,
                taxable_allowance: employee?.taxable_allowance ?? current.taxable_allowance,
                non_taxable_allowance: employee?.non_taxable_allowance ?? current.non_taxable_allowance,
                other_deductions: employee?.other_deductions ?? current.other_deductions,
              }));
            }}
          >
            <option value="">{t('selectEmployee')}</option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.full_name} ({employee.employee_code})
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('basicSalary')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.basic_salary}
            onChange={(event) => updateField('basic_salary', event.target.value)}
          />
        </label>

        <div className="split split-three">
          <label>
            {t('taxableAllowance')}
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.taxable_allowance}
              onChange={(event) => updateField('taxable_allowance', event.target.value)}
            />
          </label>

          <label>
            {t('nonTaxableAllowance')}
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.non_taxable_allowance}
              onChange={(event) => updateField('non_taxable_allowance', event.target.value)}
            />
          </label>

          <label>
            {t('otherDeductions')}
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.other_deductions}
              onChange={(event) => updateField('other_deductions', event.target.value)}
            />
          </label>
        </div>

        <label>
          {t('payrollMonth')}
          <input
            value={form.payroll_month}
            onChange={(event) => updateField('payroll_month', event.target.value)}
          />
        </label>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('calculating') : t('calculatePayroll')}
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={onBatchRun}
          disabled={isSubmitting || !employees.length}
        >
          {isSubmitting ? t('running') : t('runMonthlyBatch')}
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={onExportPayslip}
          disabled={!resultRecordId}
        >
          {t('exportPayslip')}
        </button>

        {error ? <p className="error">{error}</p> : null}
        <p className="helper">{t('printHint')}</p>
      </form>

      <article className="card results">
        <h2>{t('calculationResult')}</h2>
        {result ? (
          <dl>
            <div>
              <dt>{t('employee')}</dt>
              <dd>{result.employee_name}</dd>
            </div>
            <div>
              <dt>{t('gross')}</dt>
              <dd>{formatCurrency(result.gross, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('taxableAllowance')}</dt>
              <dd>{formatCurrency(result.taxable_allowance, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('nonTaxableAllowance')}</dt>
              <dd>{formatCurrency(result.non_taxable_allowance, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('otherDeductions')}</dt>
              <dd>{formatCurrency(result.other_deductions, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('incomeTax')}</dt>
              <dd>{formatCurrency(result.income_tax, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('employeePension')}</dt>
              <dd>{formatCurrency(result.pension_employee, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('employerPension')}</dt>
              <dd>{formatCurrency(result.pension_employer, activeLanguage)}</dd>
            </div>
            <div className="net">
              <dt>{t('netPay')}</dt>
              <dd>{formatCurrency(result.net_pay, activeLanguage)}</dd>
            </div>
            <div>
              <dt>{t('payoutStatus')}</dt>
              <dd>{result.payout_status || t('notAvailable')}</dd>
            </div>
          </dl>
        ) : (
          <p>{t('noCalculation')}</p>
        )}

        {batchResults.length ? (
          <div className="batch-results">
            <h3>{t('latestBatchRun')}</h3>
            <ul className="list compact-list">
              {batchResults.map((item) => (
                <li key={`${item.employee_name}-${item.net_pay}`}>
                  <div>
                    <strong>{item.employee_name}</strong>
                    <span>
                      {t('gross')} {formatCurrency(item.gross, activeLanguage)} • {t('netPay')} {formatCurrency(item.net_pay, activeLanguage)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </article>
    </>
  );
};

export interface PayrollHistoryItem {
  id: number | string;
  employee_name: string;
  payroll_month?: string | null;
  gross_pay: number;
  taxable_allowance: number;
  non_taxable_allowance: number;
  other_deductions: number;
  pension_employee: number;
  income_tax: number;
  net_pay: number;
  payout_status?: string | null;
}

export interface PayrollHistoryCardProps {
  history: PayrollHistoryItem[];
  payrollMonth: string;
  activeLanguage: string;
  canRunFinanceExports: boolean;
  showPayoutExport: boolean;
  onExportCsv: () => void;
  onExportErca: () => void;
  onExportPayouts: () => void;
  onExportCostCenters: () => void;
  onExportPayslip: (id: number | string, employeeName: string) => void;
}

export const PayrollHistoryCard: FC<PayrollHistoryCardProps> = ({
  history,
  payrollMonth,
  activeLanguage,
  canRunFinanceExports,
  showPayoutExport,
  onExportCsv,
  onExportErca,
  onExportPayouts,
  onExportCostCenters,
  onExportPayslip,
}) => {
  const { t } = useTranslation();

  return (
    <article className="card history-card">
      <div className="section-header">
        <h2>{t('payrollHistory')}</h2>
        <div className="section-actions">
          <button type="button" className="secondary-button" onClick={onExportCsv} disabled={!history.length}>
            {t('exportCsv')}
          </button>
          {canRunFinanceExports ? (
            <button
              type="button"
              className="secondary-button"
              onClick={onExportErca}
              disabled={!payrollMonth}
            >
              {t('exportErca')}
            </button>
          ) : null}
          {showPayoutExport ? (
            <button
              type="button"
              className="secondary-button"
              onClick={onExportPayouts}
              disabled={!payrollMonth}
            >
              {t('exportPayouts')}
            </button>
          ) : null}
          {canRunFinanceExports ? (
            <button
              type="button"
              className="secondary-button"
              onClick={onExportCostCenters}
              disabled={!payrollMonth}
            >
              {t('exportCostCenters')}
            </button>
          ) : null}
        </div>
      </div>
      {history.length ? (
        <div className="history-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t('employee')}</th>
                <th>{t('month')}</th>
                <th>{t('gross')}</th>
                <th>{t('allowances')}</th>
                <th>{t('deductions')}</th>
                <th>{t('tax')}</th>
                <th>{t('netPay')}</th>
                <th>{t('payoutStatus')}</th>
                {canRunFinanceExports ? <th>{t('payslip')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td>{item.employee_name}</td>
                  <td>{item.payroll_month || t('notAvailable')}</td>
                  <td>{formatCurrency(item.gross_pay, activeLanguage)}</td>
                  <td>{formatCurrency(item.taxable_allowance + item.non_taxable_allowance, activeLanguage)}</td>
                  <td>{formatCurrency(item.other_deductions + item.pension_employee, activeLanguage)}</td>
                  <td>{formatCurrency(item.income_tax, activeLanguage)}</td>
                  <td>{formatCurrency(item.net_pay, activeLanguage)}</td>
                  <td>{item.payout_status || t('notAvailable')}</td>
                  {canRunFinanceExports ? (
                    <td>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => onExportPayslip(item.id, item.employee_name)}
                      >
                        {t('exportPayslip')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>{t('noHistory')}</p>
      )}
    </article>
  );
};

export interface CompensationCycleFormState {
  name: string;
  effective_date: string;
  budget_amount: string | number;
  status: string;
}

export interface CompensationCycleItem {
  id: number | string;
  name: string;
  effective_date: string;
  budget_amount: number;
  status: string;
  proposed_total?: number;
  approved_total?: number;
  applied_total?: number;
}

export interface CompensationAdjustmentItem {
  id: number | string;
  employee_name: string;
  employee_code: string;
  department?: string | null;
  position?: string | null;
  cycle_name?: string | null;
  current_basic_salary: number;
  proposed_basic_salary: number;
  current_taxable_allowance: number;
  proposed_taxable_allowance: number;
  merit_percent: number;
  annualized_salary_delta: number;
  lump_sum_bonus: number;
  total_first_year_cost: number;
  rationale?: string | null;
  status: string;
  decision_reason?: string | null;
}

export interface CompensationPlanningCardProps {
  cycleForm: CompensationCycleFormState;
  setCycleForm: Dispatch<SetStateAction<CompensationCycleFormState>>;
  cycles: CompensationCycleItem[];
  adjustments: CompensationAdjustmentItem[];
  isSubmitting: boolean;
  onCycleSubmit: FormEventHandler<HTMLFormElement>;
  onAdjustmentDecision: (adjustmentId: number | string, decision: 'approve' | 'reject' | 'apply') => void | Promise<void>;
  activeLanguage: string;
}

export const CompensationPlanningCard: FC<CompensationPlanningCardProps> = ({
  cycleForm,
  setCycleForm,
  cycles,
  adjustments,
  isSubmitting,
  onCycleSubmit,
  onAdjustmentDecision,
  activeLanguage,
}) => (
  <article className="card list-card">
    <div className="section-header">
      <h2>Compensation Planning</h2>
      <span className="helper">{adjustments.filter((item) => item.status === 'proposed').length} pending</span>
    </div>
    <form className="subcard" onSubmit={onCycleSubmit}>
      <label>
        Cycle name
        <input
          value={cycleForm.name}
          onChange={(event) => setCycleForm((current) => ({ ...current, name: event.target.value }))}
        />
      </label>
      <div className="split split-three">
        <label>
          Effective date
          <input
            value={cycleForm.effective_date}
            onChange={(event) => setCycleForm((current) => ({ ...current, effective_date: event.target.value }))}
          />
        </label>
        <label>
          Budget
          <input
            type="number"
            min="0"
            step="0.01"
            value={cycleForm.budget_amount}
            onChange={(event) => setCycleForm((current) => ({ ...current, budget_amount: event.target.value }))}
          />
        </label>
        <label>
          Status
          <select
            value={cycleForm.status}
            onChange={(event) => setCycleForm((current) => ({ ...current, status: event.target.value }))}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>
      <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Create compensation cycle'}</button>
    </form>

    {cycles.length ? (
      <ul className="list compact-list">
        {cycles.map((cycle) => (
          <li key={cycle.id}>
            <div>
              <strong>{cycle.name}</strong>
              <span>
                Effective {cycle.effective_date} • {cycle.status} • budget {formatCurrency(cycle.budget_amount, activeLanguage)} •{' '}
                proposed {formatCurrency(cycle.proposed_total || 0, activeLanguage)} • approved {formatCurrency(cycle.approved_total || 0, activeLanguage)} • applied {formatCurrency(cycle.applied_total || 0, activeLanguage)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No compensation cycles configured yet.</p>
    )}

    <h3>Adjustment Queue</h3>
    {adjustments.length ? (
      <ul className="list compact-list">
        {adjustments.map((adjustment) => (
          <li key={adjustment.id}>
            <div>
              <strong>{adjustment.employee_name}</strong>
              <span>
                {adjustment.employee_code} • {adjustment.cycle_name || 'No cycle'} • salary {formatCurrency(adjustment.current_basic_salary, activeLanguage)} to {formatCurrency(adjustment.proposed_basic_salary, activeLanguage)} •{' '}
                {Number(adjustment.merit_percent).toFixed(2)}% • first-year cost {formatCurrency(adjustment.total_first_year_cost, activeLanguage)} • {adjustment.status}
              </span>
              {adjustment.rationale ? <span>{adjustment.rationale}</span> : null}
              {adjustment.decision_reason ? <span>Decision: {adjustment.decision_reason}</span> : null}
            </div>
            <div className="inline-actions">
              {adjustment.status === 'proposed' ? (
                <>
                  <button type="button" className="secondary-button" onClick={() => void onAdjustmentDecision(adjustment.id, 'approve')}>
                    Approve
                  </button>
                  <button type="button" className="secondary-button" onClick={() => void onAdjustmentDecision(adjustment.id, 'reject')}>
                    Reject
                  </button>
                </>
              ) : null}
              {adjustment.status === 'approved' ? (
                <button type="button" className="secondary-button" onClick={() => void onAdjustmentDecision(adjustment.id, 'apply')}>
                  Apply to payroll profile
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No compensation adjustments submitted yet.</p>
    )}
  </article>
);
